import {
  DEMO_ASSUMPTIONS,
  DEMO_DEFECT_TYPES,
  DEMO_PERIODS,
  DEMO_PRODUCTS,
  DEMO_STATIONS,
  PERCEPTION_DEMO_DATA,
} from '../../data/perception-demo-data';

const sum = (items, field) => items.reduce((total, item) => total + (item[field] ?? 0), 0);
const percentage = (part, total) => total > 0 ? part / total * 100 : 0;
const round = (value, decimals = 1) => Number(value.toFixed(decimals));
const getDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const dateFromKey = (key) => new Date(`${key}T12:00:00`);
const shiftDate = (key, days) => {
  const date = dateFromKey(key);
  date.setDate(date.getDate() + days);
  return getDateKey(date);
};

export const formatDemoNumber = (value, digits = 0) => new Intl.NumberFormat('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number.isFinite(value) ? value : 0);
export const formatDemoCurrency = (value, compact = false) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: compact ? 0 : 0, notation: compact ? 'compact' : 'standard' }).format(Number.isFinite(value) ? value : 0);
export const formatDemoPercent = (value, digits = 1) => `${formatDemoNumber(value, digits)}%`;
export const formatDemoDate = (key) => new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(dateFromKey(key));
export const formatDemoTimestamp = (value) => {
  const [date, time] = value.split(' ');
  return `${formatDemoDate(date)} · ${time.slice(0, 5)}`;
};

export const getDemoPeriod = (periodId) => DEMO_PERIODS.find(({ id }) => id === periodId) ?? DEMO_PERIODS[1];

const aggregate = (records) => {
  const assembled = sum(records, 'assembled');
  const planned = sum(records, 'planned');
  const confirmed = sum(records, 'confirmedDefects');
  const falsePositives = sum(records, 'falsePositives');
  const missed = sum(records, 'missedDefects');
  const corrected = sum(records, 'corrected');
  const rejected = sum(records, 'rejected');
  const scheduledMinutes = sum(records, 'scheduledMinutes');
  const uptimeMinutes = sum(records, 'uptimeMinutes');
  const stationDays = new Set(records.filter(({ assembled: count }) => count > 0).map(({ date, stationId }) => `${date}-${stationId}`)).size;
  const productionDays = new Set(records.filter(({ assembled: count }) => count > 0).map(({ date }) => date)).size;
  const savedHours = assembled * DEMO_ASSUMPTIONS.minutesSavedPerWatch / 60;
  const baselineExpectedDefects = assembled * DEMO_ASSUMPTIONS.baselineDefectRate / 100;
  const preventedDefects = baselineExpectedDefects - confirmed - missed;
  const avoidedScrapEquivalent = baselineExpectedDefects - rejected - missed;
  const scrapSavings = avoidedScrapEquivalent * DEMO_ASSUMPTIONS.avoidedCostPerAtRiskWatch;
  const laborSavings = savedHours * DEMO_ASSUMPTIONS.hourlyLaborCost;
  const grossSavings = scrapSavings + laborSavings;
  // Attribute a station-day license proportionally to the model's production mix.
  const operatingCost = sum(records, 'softwareCost');
  const netSavings = grossSavings - operatingCost;

  return {
    assembled, planned, confirmed, falsePositives, missed, corrected, rejected,
    alerts: confirmed + falsePositives,
    productionDays, stationDays, uptimeMinutes, scheduledMinutes,
    throughputPerDay: productionDays > 0 ? assembled / productionDays : 0,
    firstPassYield: percentage(Math.max(0, assembled - confirmed - missed), assembled),
    finalCompliance: percentage(Math.max(0, assembled - rejected - missed), assembled),
    defectRate: percentage(confirmed + missed, assembled),
    scrapRate: percentage(rejected, assembled),
    correctionRate: percentage(corrected, confirmed),
    precision: percentage(confirmed, confirmed + falsePositives),
    recall: percentage(confirmed, confirmed + missed),
    availability: percentage(uptimeMinutes, scheduledMinutes),
    planAttainment: percentage(assembled, planned),
    alertsPer100: percentage(confirmed + falsePositives, assembled),
    responseMs: assembled > 0 ? records.reduce((total, record) => total + record.responseMs * record.assembled, 0) / assembled : 0,
    savedHours, scrapSavings, laborSavings, grossSavings, operatingCost, netSavings,
    baselineExpectedDefects, preventedDefects, avoidedScrapEquivalent,
    costAllocation: stationDays > 0 ? operatingCost / (stationDays * DEMO_ASSUMPTIONS.softwareCostPerStationDay) : 0,
    roi: operatingCost > 0 ? netSavings / operatingCost * 100 : 0,
  };
};

const groupBy = (records, getKey) => records.reduce((groups, record) => {
  const key = getKey(record);
  if (!groups[key]) groups[key] = [];
  groups[key].push(record);
  return groups;
}, {});

const makeDailySeries = (records, start, end) => {
  const groups = groupBy(records, ({ date }) => date);
  const days = [];

  for (let date = start; date <= end; date = shiftDate(date, 1)) {
    const metrics = aggregate(groups[date] ?? []);
    days.push({ date, label: formatDemoDate(date), ...metrics, hasProduction: metrics.assembled > 0 });
  }

  return days;
};

const makeWeeklySeries = (dailySeries) => {
  const weeks = [];

  for (let offset = 0; offset < dailySeries.length; offset += 7) {
    const days = dailySeries.slice(offset, offset + 7);
    const active = days.filter(({ hasProduction }) => hasProduction);
    weeks.push({
      label: `${formatDemoDate(days[0].date)}–${formatDemoDate(days[days.length - 1].date)}`,
      precision: sum(active, 'alerts') > 0 ? round(percentage(sum(active, 'confirmed'), sum(active, 'alerts'))) : null,
      availability: active.length ? round(percentage(sum(active, 'uptimeMinutes'), sum(active, 'scheduledMinutes'))) : null,
      firstPassYield: active.length ? round(percentage(sum(active, 'assembled') - sum(active, 'confirmed') - sum(active, 'missed'), sum(active, 'assembled'))) : null,
    });
  }

  return weeks;
};

const buildStations = (records, events) => DEMO_STATIONS.map((station) => {
  const rows = records.filter(({ stationId }) => stationId === station.id);
  const stationEvents = events.filter(({ stationId, status }) => stationId === station.id && status !== 'Dismissed');
  const counts = groupBy(stationEvents, ({ defectId }) => defectId);
  const topDefectId = Object.keys(counts).sort((a, b) => counts[b].length - counts[a].length)[0];
  const topDefect = DEMO_DEFECT_TYPES.find(({ id }) => id === topDefectId);
  return { ...station, ...aggregate(rows), topDefect: topDefect?.name ?? 'No deviations', eventCount: stationEvents.length };
});

const buildDefects = (events, assembled) => {
  const confirmed = events.filter(({ status }) => status !== 'Dismissed');
  return DEMO_DEFECT_TYPES.map((defect) => {
    const matching = confirmed.filter(({ defectId }) => defectId === defect.id);
    return {
      ...defect,
      count: matching.length,
      share: percentage(matching.length, confirmed.length),
      per100: percentage(matching.length, assembled),
      corrected: matching.filter(({ status }) => status === 'Corrected').length,
    };
  }).sort((a, b) => b.count - a.count);
};

const buildStatus = (events) => [
  { name: 'Corrected in process', count: events.filter(({ status }) => status === 'Corrected').length, color: '#4164FD' },
  { name: 'Rejected', count: events.filter(({ status }) => status === 'Rejected').length, color: '#F28725' },
  { name: 'False alarms dismissed', count: events.filter(({ status }) => status === 'Dismissed').length, color: '#4CBEBB' },
];

export const getPerceptionDemoReport = ({ period = '30d', station = 'all', product = 'all' }) => {
  const days = getDemoPeriod(period).days;
  const { daily, events, referenceDate } = PERCEPTION_DEMO_DATA;
  const start = shiftDate(referenceDate, 1 - days);
  const previousStart = shiftDate(start, -days);
  const scopeMatch = (record) => (station === 'all' || record.stationId === station) && (product === 'all' || record.productId === product);
  const scopedDaily = daily.filter(scopeMatch);
  const scopedEvents = events.filter(scopeMatch);
  const records = scopedDaily.filter(({ date }) => date >= start && date <= referenceDate);
  const previousRecords = scopedDaily.filter(({ date }) => date >= previousStart && date < start);
  const recentEvents = scopedEvents.filter(({ date }) => date >= start && date <= referenceDate);
  const metrics = aggregate(records);
  const previous = aggregate(previousRecords);
  const dailySeries = makeDailySeries(records, start, referenceDate);
  const weeklySeries = makeWeeklySeries(dailySeries);
  const stations = buildStations(records, recentEvents).filter(({ id }) => station === 'all' || id === station);
  const products = DEMO_PRODUCTS.map((item) => ({ ...item, ...aggregate(records.filter(({ productId }) => productId === item.id)) }))
    .filter(({ id }) => product === 'all' || id === product);
  const shifts = ['A', 'B', 'C'].map((name) => ({ name, ...aggregate(records.filter(({ shift }) => shift === name)) }));
  const defects = buildDefects(recentEvents, metrics.assembled);
  const status = buildStatus(recentEvents);
  const targetDefectRate = DEMO_ASSUMPTIONS.baselineDefectRate;
  const baselineExpectedDefects = metrics.baselineExpectedDefects;

  let cumulativeSavings = 0;
  let cumulativeCosts = 0;
  const cumulative = dailySeries.map((day) => {
    cumulativeSavings += day.grossSavings;
    cumulativeCosts += day.operatingCost;
    return { label: day.label, benefits: round(cumulativeSavings, 0), costs: round(cumulativeCosts, 0) };
  });

  const confidenceBands = [
    { name: '60–79%', min: 0.6, max: 0.8 },
    { name: '80–89%', min: 0.8, max: 0.9 },
    { name: '90–94%', min: 0.9, max: 0.95 },
    { name: '95–100%', min: 0.95, max: 1.01 },
  ].map((band) => ({ ...band, count: recentEvents.filter(({ confidence }) => confidence >= band.min && confidence < band.max).length }));

  return {
    scope: { period, station, product, days, start, end: referenceDate },
    metrics, previous, dailySeries, weeklySeries, stations, products, shifts, defects,
    status, events: recentEvents, cumulative, confidenceBands,
    baseline: { defectRate: targetDefectRate, expectedDefects: baselineExpectedDefects, avoidedDefects: Math.max(0, Math.round(metrics.preventedDefects)) },
    assumptions: DEMO_ASSUMPTIONS,
  };
};

export const getDemoRelativeChange = (current, previous) => previous > 0 ? (current - previous) / previous * 100 : null;
export const getDemoTrendText = (current, previous) => {
  const change = getDemoRelativeChange(current, previous);
  if (change === null) return 'No prior comparison';
  return `${change >= 0 ? '+' : ''}${formatDemoNumber(change, 1)}% vs. prior period`;
};
